'use client';

import { useState } from 'react';

import { cantonLabels, qualificationLabels } from '@selenos/vertical-spitex';

/**
 * Salary data by qualification level (CHF/year).
 * Source: spitex-caregiver-recruitment skill.
 */
const salaryData: Record<
  string,
  {
    entry: [number, number];
    experienced: [number, number];
    senior: [number, number];
  }
> = {
  hf_pflege: {
    entry: [75_000, 80_000],
    experienced: [85_000, 95_000],
    senior: [95_000, 110_000],
  },
  fh_pflege: {
    entry: [78_000, 85_000],
    experienced: [90_000, 100_000],
    senior: [100_000, 120_000],
  },
  fage: {
    entry: [52_000, 58_000],
    experienced: [60_000, 70_000],
    senior: [70_000, 78_000],
  },
  fabe: {
    entry: [50_000, 55_000],
    experienced: [58_000, 65_000],
    senior: [65_000, 72_000],
  },
  ags: {
    entry: [42_000, 48_000],
    experienced: [48_000, 55_000],
    senior: [55_000, 60_000],
  },
  pflegehilfe_srk: {
    entry: [38_000, 42_000],
    experienced: [42_000, 48_000],
    senior: [48_000, 52_000],
  },
  no_qualification: {
    entry: [35_000, 40_000],
    experienced: [40_000, 45_000],
    senior: [40_000, 45_000],
  },
};

/**
 * Canton salary modifiers (percentage adjustment).
 * Source: spitex-caregiver-recruitment skill.
 */
const cantonModifiers: Record<string, number> = {
  ZH: 0.075,
  BS: 0.065,
  GE: 0.1,
  VD: 0.05,
  BE: 0,
  SG: -0.01,
  TG: -0.015,
  LU: 0.015,
  AG: 0.02,
  BL: 0.04,
  SO: 0.01,
  FR: 0.02,
  NE: 0.02,
  JU: 0,
  GL: 0,
  GR: 0,
  SH: 0.01,
  SZ: 0.03,
  NW: 0.02,
  OW: 0.01,
  UR: 0,
  TI: 0.01,
  VS: 0,
  ZG: 0.06,
  AI: 0,
  AR: 0,
};

function formatCHF(amount: number): string {
  return `CHF ${Math.round(amount).toLocaleString('de-CH')}`;
}

/** Supported qualification keys for the salary calculator dropdown. */
const qualificationKeys = [
  'hf_pflege',
  'fh_pflege',
  'fage',
  'fabe',
  'ags',
  'pflegehilfe_srk',
  'no_qualification',
] as const;

/** Canton keys in alphabetical order. */
const cantonKeys = [
  'AG',
  'AI',
  'AR',
  'BE',
  'BL',
  'BS',
  'FR',
  'GE',
  'GL',
  'GR',
  'JU',
  'LU',
  'NE',
  'NW',
  'OW',
  'SG',
  'SH',
  'SO',
  'SZ',
  'TG',
  'TI',
  'UR',
  'VD',
  'VS',
  'ZG',
  'ZH',
] as const;

export function SalaryCalculator() {
  const [canton, setCanton] = useState<string>('ZH');
  const [qualification, setQualification] = useState<string>('fage');

  const data = salaryData[qualification];
  const modifier = cantonModifiers[canton] ?? 0;

  // Use "experienced" range as the primary display
  const minSalary = data ? data.entry[0] * (1 + modifier) : 0;
  const maxSalary = data ? data.senior[1] * (1 + modifier) : 0;

  // Experience breakdown
  const entryMin = data ? data.entry[0] * (1 + modifier) : 0;
  const entryMax = data ? data.entry[1] * (1 + modifier) : 0;
  const expMin = data ? data.experienced[0] * (1 + modifier) : 0;
  const expMax = data ? data.experienced[1] * (1 + modifier) : 0;
  const seniorMin = data ? data.senior[0] * (1 + modifier) : 0;
  const seniorMax = data ? data.senior[1] * (1 + modifier) : 0;

  return (
    <section className="bg-muted/50 w-full px-4 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <p className="text-muted-foreground mb-2 text-center text-sm tracking-[0.2em] uppercase">
          Gehaltsrechner
        </p>
        <h2 className="text-foreground mb-12 text-center text-2xl font-bold sm:text-3xl">
          Was verdienen Sie bei der Spitex?
        </h2>

        {/* Inputs */}
        <div className="mx-auto max-w-lg">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Canton select */}
            <div>
              <label
                htmlFor="salary-canton"
                className="text-foreground block text-sm font-medium"
              >
                Kanton
              </label>
              <select
                id="salary-canton"
                value={canton}
                onChange={(e) => setCanton(e.target.value)}
                className="bg-card text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
              >
                {cantonKeys.map((key) => (
                  <option key={key} value={key}>
                    {cantonLabels[key] ?? key}
                  </option>
                ))}
              </select>
            </div>

            {/* Qualification select */}
            <div>
              <label
                htmlFor="salary-qualification"
                className="text-foreground block text-sm font-medium"
              >
                Qualifikation
              </label>
              <select
                id="salary-qualification"
                value={qualification}
                onChange={(e) => setQualification(e.target.value)}
                className="bg-card text-foreground border-border mt-1 block w-full rounded-lg border px-3 py-2.5 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
              >
                {qualificationKeys.map((key) => (
                  <option key={key} value={key}>
                    {qualificationLabels[key] ?? key}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="mt-10 flex flex-col items-center">
          <p className="text-muted-foreground text-sm">
            Jaehrlicher Bruttolohn in {cantonLabels[canton] ?? canton}
          </p>
          <p
            className="mt-2 text-4xl font-bold sm:text-5xl"
            style={{ color: 'var(--landing-primary)' }}
          >
            {formatCHF(minSalary)} – {formatCHF(maxSalary)}
          </p>
        </div>

        {/* Breakdown by experience level */}
        <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="bg-card rounded-xl border p-5 text-center">
            <p className="text-muted-foreground text-xs tracking-wider uppercase">
              Berufseinsteiger
            </p>
            <p className="text-foreground mt-2 text-lg font-semibold">
              {formatCHF(entryMin)} – {formatCHF(entryMax)}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">pro Jahr</p>
          </div>

          <div
            className="rounded-xl border-2 p-5 text-center"
            style={{ borderColor: 'var(--landing-primary)' }}
          >
            <p
              className="text-xs tracking-wider uppercase"
              style={{ color: 'var(--landing-primary)' }}
            >
              5+ Jahre Erfahrung
            </p>
            <p className="text-foreground mt-2 text-lg font-semibold">
              {formatCHF(expMin)} – {formatCHF(expMax)}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">pro Jahr</p>
          </div>

          <div className="bg-card rounded-xl border p-5 text-center">
            <p className="text-muted-foreground text-xs tracking-wider uppercase">
              Senior / Leitung
            </p>
            <p className="text-foreground mt-2 text-lg font-semibold">
              {formatCHF(seniorMin)} – {formatCHF(seniorMax)}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">pro Jahr</p>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-muted-foreground mt-8 text-center text-xs">
          Richtwerte basierend auf Branchendaten. Das tatsaechliche Gehalt
          haengt von Arbeitgeber, Erfahrung und individueller Vereinbarung ab.
          Bruttolohn ohne Zulagen.
        </p>
      </div>
    </section>
  );
}
